import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Stack,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VideocamIcon from "@mui/icons-material/Videocam";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";

function getYoutubeEmbedUrl(url = "") {
  try {
    const parsed = new URL(url);

    if (parsed.hostname.includes("youtube.com")) {
      const v = parsed.searchParams.get("v");
      if (v) return `https://www.youtube.com/embed/${v}`;
      const parts = parsed.pathname.split("/").filter(Boolean);
      const maybeId = parts[parts.length - 1];
      if (maybeId) return `https://www.youtube.com/embed/${maybeId}`;
    }

    if (parsed.hostname.includes("youtu.be")) {
      const id = parsed.pathname.replace("/", "");
      if (id) return `https://www.youtube.com/embed/${id}`;
    }

    return "";
  } catch {
    return "";
  }
}

export default function GalleryVideoModal({
  open,
  onClose,
  onSaved,
  mode = "create",
  item = null,
  backendBase = "",
  token = "",
  notify,
}) {
  const [src, setSrc] = useState("");
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setSrc(item?.src || "");
      setTitle(item?.title || "");
      setSaving(false);
      setErr("");
    }
  }, [open, item]);

  const embedUrl = useMemo(() => getYoutubeEmbedUrl(src), [src]);

  const handleSave = async () => {
    if (!src.trim()) {
      const msg = "Συμπλήρωσε YouTube URL.";
      setErr(msg);
      notify?.("error", msg);
      return;
    }

    if (!embedUrl) {
      const msg = "Το URL δεν είναι έγκυρο YouTube link.";
      setErr(msg);
      notify?.("error", msg);
      return;
    }

    setSaving(true);
    setErr("");

    try {
      const url =
        mode === "edit" && item?._id
          ? `${backendBase}/api/admin/images/client/gallery/${item._id}/video`
          : `${backendBase}/api/admin/images/client/gallery/video`;

      const method = mode === "edit" ? "patch" : "post";

      const res = await axios({
        url,
        method,
        data: {
          src: src.trim(),
          title: title.trim(),
        },
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const savedItem = res.data?.data || res.data?.item || res.data;
      const message =
        res.data?.message ||
        (mode === "edit" ? "Το video ενημερώθηκε επιτυχώς." : "Το video προστέθηκε επιτυχώς.");

      notify?.("success", message);
      onSaved?.();
      onClose?.();
    } catch (e) {
      console.error("Gallery video save failed:", e);
      const message =
        e?.response?.data?.message ||
        e?.message ||
        "Αποτυχία αποθήκευσης video.";
      setErr(message);
      notify?.("error", message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "#1a1a2e",
          color: "#fff",
          borderRadius: 3,
          border: "1px solid rgba(255,255,255,0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <VideocamIcon sx={{ color: "#fff" }} />
          <Typography fontWeight={900} variant="h6">
            {mode === "edit" ? "Αντικατάσταση Video" : "Νέο Video Gallery"}
          </Typography>
        </Stack>

        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: "rgba(255,255,255,0.6)" }}
          disabled={saving}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5 }}>
        <Stack spacing={2.5}>
          <TextField
            label="YouTube URL"
            value={src}
            onChange={(e) => setSrc(e.target.value)}
            fullWidth
            variant="outlined"
            InputLabelProps={{ style: { color: "rgba(255,255,255,0.72)" } }}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: "#fff",
                "& fieldset": { borderColor: "rgba(255,255,255,0.24)" },
                "&:hover fieldset": { borderColor: "rgba(255,255,255,0.4)" },
                "&.Mui-focused fieldset": { borderColor: "#fff" },
              },
            }}
          />

          <TextField
            label="Τίτλος"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            variant="outlined"
            InputLabelProps={{ style: { color: "rgba(255,255,255,0.72)" } }}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: "#fff",
                "& fieldset": { borderColor: "rgba(255,255,255,0.24)" },
                "&:hover fieldset": { borderColor: "rgba(255,255,255,0.4)" },
                "&.Mui-focused fieldset": { borderColor: "#fff" },
              },
            }}
          />

          <Box
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              border: "1px solid rgba(255,255,255,0.16)",
              bgcolor: "rgba(255,255,255,0.04)",
              minHeight: 220,
            }}
          >
            {embedUrl ? (
              <Box sx={{ position: "relative", width: "100%", height: 300, bgcolor: "#000" }}>
                <Box
                  component="iframe"
                  src={embedUrl}
                  title={title || "YouTube preview"}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  sx={{ width: "100%", height: "100%", border: 0 }}
                />
              </Box>
            ) : (
              <Stack
                spacing={1}
                alignItems="center"
                justifyContent="center"
                sx={{ minHeight: 220, color: "rgba(255,255,255,0.75)" }}
              >
                <PlayCircleOutlineIcon sx={{ fontSize: 42 }} />
                <Typography>Δώσε έγκυρο YouTube link για προεπισκόπηση</Typography>
              </Stack>
            )}
          </Box>

          {err && (
            <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>
              {err}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          pt: 1.5,
          borderTop: "1px solid rgba(255,255,255,0.08)",
          gap: 1,
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={saving}
          sx={{ borderColor: "rgba(255,255,255,0.3)", color: "#fff", flex: 1 }}
        >
          Ακύρωση
        </Button>

        <Button variant="contained" onClick={handleSave} disabled={!src || saving} sx={{ flex: 1 }}>
          {saving ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} color="inherit" />
              <span>Αποθήκευση...</span>
            </Stack>
          ) : mode === "edit" ? (
            "Αντικατάσταση"
          ) : (
            "Αποθήκευση"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
